/* V23.10 compact: photo/name variant sheet, independent device controls and modal suspension.
   V23.6 scroll threshold preserved; the only submit target is Shopify's existing native product form.
   No parallel /cart/add request, no hard-coded variant, no automatic order. */
(() => {
  'use strict';
  if (customElements.get('ov-sticky-buy')) return;
  class OvStickyBuy extends HTMLElement {
    connectedCallback() {
      if (this._abort) return;
      this._abort = new AbortController();
      const options = { signal: this._abort.signal };
      this._button = this.querySelector('[data-ovs-button]');
      this._select = this.querySelector('[data-ovs-select]');
      this._qty = this.querySelector('[data-ovs-quantity]');
      this._status = this.querySelector('[data-ovs-status]');
      this._minus = this.querySelector('[data-ovs-minus]');
      this._plus = this.querySelector('[data-ovs-plus]');
      this._price = this.querySelector('[data-ovs-price]');
      this._compare = this.querySelector('[data-ovs-compare]');
      this._saving = this.querySelector('[data-ovs-saving]');
      this._variantText = this.querySelector('[data-ovs-variant]');
      this._photo = this.querySelector('[data-ovs-photo]');
      this._defaultPhoto = this._photo?.getAttribute('src');
      this._trigger = this.querySelector('[data-ovs-picker-trigger]');
      this._picker = document.getElementById(this.dataset.pickerId);
      this._displayMap = new Map();
      try { JSON.parse(this.querySelector('[data-ovs-display]')?.textContent || '[]').forEach(v => this._displayMap.set(String(v.id), v)); } catch (_) {}
      this._inboxObservers = []; this._inboxRoots = new WeakSet();
      this._spacer = document.querySelector(`[data-ovs-spacer="${this.id}"]`);
      this._media = window.matchMedia('(max-width:749.98px)');
      this._frame = 0;
      try { this._variants = JSON.parse(this.querySelector('[data-ovs-variants]')?.textContent || '[]'); }
      catch (_) { this._variants = []; }
      this._scheduleBound = () => this.schedule();
      this._media.addEventListener('change', this._scheduleBound, options);
      window.addEventListener('scroll', this._scheduleBound, { ...options, passive: true });
      window.addEventListener('resize', this._scheduleBound, { ...options, passive: true });
      window.addEventListener('pageshow', this._scheduleBound, options);
      window.addEventListener('load', this._scheduleBound, options);
      ['variant:change', 'ot:pdp-state', 'shopify:section:load', 'shopify:section:reorder'].forEach(event => document.addEventListener(event, this._scheduleBound, options));
      ['input', 'change'].forEach(event => document.addEventListener(event, eventObject => {
        if (eventObject.target?.closest('variant-selects') && !eventObject.detail?.ovStickyFocus) {
          this._pendingId=null;clearTimeout(this._pendingTimer);if(this._status)this._status.textContent='';
        }
        if (eventObject.target?.closest('product-info') === this._info || eventObject.target?.getAttribute('form') === this._form?.id) this.schedule();
      }, options));
      this._select?.addEventListener('change', () => this.chooseVariant(), options);
      this._qty?.addEventListener('input', () => this.writeQuantity(false), options);
      this._qty?.addEventListener('change', () => this.writeQuantity(true), options);
      this._minus?.addEventListener('click', () => this.stepQuantity(-1), options);
      this._plus?.addEventListener('click', () => this.stepQuantity(1), options);
      this._button?.addEventListener('click', () => this.submit(), options);
      this._trigger?.addEventListener('click', () => this.openPicker(), options);
      this._picker?.addEventListener('click', event => {
        const close = event.target.closest?.('[data-ovs-picker-close]');
        if (close) { this.closePicker(); return; }
        if (event.target === this._picker) {
          const box = this._picker.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.closePicker();
          return;
        }
        const option = event.target.closest?.('[data-ovs-option]');
        if (!option || option.disabled || !this._select) return;
        const id = option.dataset.ovsOption;
        this.closePicker();
        if (this._form?.querySelector('[name="id"]')?.value === id && !this._pendingId) return;
        this._select.value = id;
        this.chooseVariant();
      }, options);
      this._picker?.addEventListener('close', () => this.cleanupPicker(), options);
      this._picker?.addEventListener('cancel', event => { event.preventDefault(); this.closePicker(); }, options);
      this._overlayObserver = new MutationObserver(records => {
        const selectors = 'dialog,cart-drawer,.drawer,product-modal,menu-drawer,header-drawer,shopify-chat,#ShopifyChat';
        const relevant = records.some(record => {
          if (record.type === 'attributes') return record.target.matches?.(selectors) || record.target.matches?.('menu-drawer details,header-drawer details');
          return [...record.addedNodes, ...record.removedNodes].some(node => node.nodeType === 1 && (node.matches(selectors) || node.querySelector(selectors)));
        });
        if (relevant) { this.observeInbox(); this.schedule(); }
      });
      this._overlayObserver.observe(document.body, { subtree: true, childList: true, attributes: true, attributeFilter: ['open','class','hidden','aria-expanded'] });
      document.addEventListener('otervano:inbox-state', this._scheduleBound, options);
      document.addEventListener('otervano:inbox-shortcut-requested', () => { this.observeInbox(); this.schedule(); }, options);
      document.addEventListener('click', event => {
        if (event.composedPath().some(el => el?.matches?.('shopify-chat,#ShopifyChat'))) { this.observeInbox(); this.schedule(); }
      }, { ...options, capture: true });
      this.observeInbox();
      if ('ResizeObserver' in window) {
        this._sizeObserver = new ResizeObserver(() => this.space());
        this._sizeObserver.observe(this);
        this._pageSizeObserver = new ResizeObserver(this._scheduleBound);
        this._pageSizeObserver.observe(document.body);
      }
      this.schedule();
    }
    disconnectedCallback() {
      this.closePicker(false);
      this._abort?.abort(); this._abort = null;
      this._overlayObserver?.disconnect();
      this._inboxObservers?.forEach(observer => observer.disconnect());
      this._observer?.disconnect(); this._sizeObserver?.disconnect();
      this._pageSizeObserver?.disconnect();
      clearTimeout(this._pendingTimer);
      if (this._frame) cancelAnimationFrame(this._frame);
      this._frame = 0;
      if (this._spacer) this._spacer.style.height = '0px';
    }
    bindMain() {
      const main = document.querySelector('#MainContent');
      const infos = [...(main?.querySelectorAll('product-info') || [])];
      const info = infos.find(item => item.dataset.productId === this.dataset.productId);
      const form = info?.querySelector('product-form form');
      const button = form?.querySelector('.product-form__submit,button[type="submit"][name="add"]');
      if (!info || !form || !button) return false;
      if (this._info !== info || this._form !== form || this._mainButton !== button) {
        this._observer?.disconnect();
        this._info = info; this._form = form; this._mainButton = button;
        this._observer = new MutationObserver(this._scheduleBound);
        this._observer.observe(info, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'aria-busy', 'class', 'value'] });
      }
      return true;
    }
    schedule() {
      if (!this._frame) this._frame = requestAnimationFrame(() => { this._frame = 0; if (this.isConnected) this.sync(); });
    }

    nativeQuantity() {
      return this._info?.querySelector('input[name="quantity"]') || [...document.querySelectorAll('input[name="quantity"]')].find(input => input.getAttribute('form') === this._form?.id);
    }
    syncControls(variant) {
      if (this._select && !this._pendingId && variant) this._select.value = String(variant.id);
      const native = this.nativeQuantity();
      if (!this._qty) return;
      for (const key of ['min','max','step']) {
        const value = native?.getAttribute(key) || (key === 'max' ? null : '1');
        if (value != null) this._qty.setAttribute(key,value); else this._qty.removeAttribute(key);
      }
      if (document.activeElement !== this._qty && !this._pendingId) this._qty.value = native?.value || '1';
      const current = Number(this._qty.value), min = Number(this._qty.min || 1), max = this._qty.max ? Number(this._qty.max) : Infinity;
      this._minus.disabled = current <= min || Boolean(this._pendingId);
      this._plus.disabled = current >= max || Boolean(this._pendingId);
    }
    writeQuantity(normalize) {
      if (!this._qty || !this.bindMain()) return;
      let native = this.nativeQuantity();
      if (!native) { native = document.createElement('input'); native.type='hidden'; native.name='quantity'; native.value='1'; this._form.append(native); }
      let value = Number(this._qty.value);
      const min = Number(native.getAttribute('min') || 1), max = native.getAttribute('max') ? Number(native.max) : Infinity;
      const step = Number(native.getAttribute('step')) || 1;
      if (normalize) {value = Math.max(min, Math.min(max, Number.isFinite(value) ? value : min));value = min + Math.floor((value-min)/step)*step;this._qty.value=String(value);}
      if (!Number.isSafeInteger(value) || value<min || value>max || (value-min)%step!==0 || this._qty.value==='') return;
      if (native.value!==String(value)) { native.value=String(value); native.dispatchEvent(new Event('input',{bubbles:true}));native.dispatchEvent(new Event('change',{bubbles:true})); }
      this.schedule();
    }
    stepQuantity(direction) {
      this.writeQuantity(true);
      if (!this._qty) return;
      try { if(direction>0)this._qty.stepUp();else this._qty.stepDown(); } catch (_) { return; }
      this.writeQuantity(true);
    }
    chooseVariant() {
      if (!this._select || !this.bindMain()) return;
      const variant = this._variants.find(v=>String(v.id)===this._select.value);
      const picker = this._info.querySelector('variant-selects');
      if (!variant || !picker) { this._mainButton.scrollIntoView({block:'center'});return; }
      const groups=[...picker.querySelectorAll('.product-form__input')].filter(group=>group.querySelector('select,input[type="radio"]'));
      const changes=variant.options.map((value,i)=> {
        const group=groups[i]; const select=group?.querySelector('select');
        if(select && [...select.options].some(o=>o.value===value))return {control:select,value};
        const radio=[...(group?.querySelectorAll('input[type="radio"]')||[])].find(r=>r.value===value);
        return radio ? {control:radio,value} : null;
      });
      if(changes.some(c=>!c)||!changes.length){this._mainButton.scrollIntoView({block:'center'});return;}
      this._pendingId=String(variant.id);
      this._button.disabled=true;this._button.textContent=this.dataset.updating;
      this._pendingQuantity=this._qty?.value || null;
      if(this._status)this._status.textContent=this.dataset.updating;
      changes.forEach(({control,value})=>{
        if(control.tagName==='SELECT'){
          control.value=value;
          // Native VariantSelects reads option[selected], not only selectedOptions.
          [...control.options].forEach(o=>o.toggleAttribute('selected',o.value===value));
        }else control.checked=true;
      });
      clearTimeout(this._pendingTimer);
      this._pendingTimer=setTimeout(()=>{
        this._pendingId=null;
        if(this._status)this._status.textContent=this.dataset.error;
        this._pendingQuantity=null;this.schedule();
      },12000);
      // One native change: Shopify updates its own form, images, price, inventory and URL.
      // Temporarily route only this native request's focus back to the sticky selector.
      // Native ProductInfo still fetches and renders the authoritative variant normally.
      const info=this._info, render=info.renderProductInfo, focusId=this.pickerMode() === 'panel' && this._trigger ? this._trigger.id : this._select.id;
      if(typeof render==='function') info.renderProductInfo=function(request){return render.call(this,{...request,targetId:focusId});};
      try {changes.at(-1).control.dispatchEvent(new CustomEvent('change',{bubbles:true,detail:{ovStickyFocus:focusId}}));}
      finally {if(typeof render==='function')info.renderProductInfo=render;}
      this.schedule();
    }

    deviceFlag(name) {
      const raw = this.dataset[name + (this._media.matches ? 'Mobile' : 'Desktop')];
      return raw !== 'false';
    }
    pickerMode() { return this.dataset[this._media.matches ? 'pickerMobile' : 'pickerDesktop'] || 'panel'; }
    setText(el, value) { if (el.textContent !== value) el.textContent = value; }
    withCurrency(text) {
      const currency = this.dataset.currency || 'USD';
      return text.includes(currency) ? text : `${text} ${currency}`;
    }
    thumbnail(src) {
      if (!src) return '';
      try { const url = new URL(src, document.baseURI); url.searchParams.set('width', '160'); return url.href; } catch (_) { return ''; }
    }
    openPicker() {
      if (!this._picker?.showModal || this.pickerMode() !== 'panel' || this.hidden || this.hasOverlay()) return;
      this._picker.showModal();
      document.documentElement.classList.add('ovs-picker-open');
      this._trigger?.setAttribute('aria-expanded', 'true');
      const selected = this._picker.querySelector('[aria-pressed="true"]:not(:disabled)') || this._picker.querySelector('[data-ovs-picker-close]');
      selected?.focus({ preventScroll: true });
      if (selected?.matches('[data-ovs-option]')) selected.scrollIntoView({block:'nearest',behavior:'instant'});
      this.schedule();
    }
    closePicker(restoreFocus = true) {
      if (!this._picker?.open) return;
      this._restorePickerFocus = restoreFocus;
      this._picker.close();
      this.cleanupPicker();
    }
    cleanupPicker() {
      document.documentElement.classList.remove('ovs-picker-open');
      this._trigger?.setAttribute('aria-expanded', 'false');
      this.inert = false; this.dataset.ovsSuspended = 'false';
      if (this._restorePickerFocus !== false && this.isConnected && !this.hasOverlay()) this._trigger?.focus({ preventScroll: true });
      this._restorePickerFocus = true;
      this.schedule();
    }
    observeInbox() {
      for (const host of document.querySelectorAll('shopify-chat,#ShopifyChat')) {
        const roots = [host,host.shadowRoot];
        if (host.shadowRoot) roots.push(...[...host.shadowRoot.querySelectorAll('shopify-chat,#ShopifyChat')].map(el => el.shadowRoot));
        for (const root of roots.filter(Boolean)) {
          if (this._inboxRoots.has(root)) continue;
          this._inboxRoots.add(root);
          const observer = new MutationObserver(() => { this.observeInbox(); this.schedule(); });
          observer.observe(root, { subtree:true,childList:true,attributes:true,attributeFilter:['class','style','hidden','open','aria-expanded','aria-label','title'] });
          this._inboxObservers.push(observer);
        }
      }
    }
    inboxOpen() {
      if (window.OtervanoInbox) return window.OtervanoInbox.isOpen();
      const visible = el => Boolean(el && !el.hidden && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
      for (const host of document.querySelectorAll('shopify-chat,#ShopifyChat')) {
        if (!visible(host)) continue;
        const roots = [host,host.shadowRoot];
        if (host.shadowRoot) roots.push(...[...host.shadowRoot.querySelectorAll('shopify-chat,#ShopifyChat')].map(el => el.shadowRoot));
        for (const root of roots.filter(Boolean)) {
          const button = root.querySelector('.chat-app > button');
          const label = `${button?.getAttribute('aria-label') || ''} ${button?.title || ''}`;
          if (button?.getAttribute('aria-expanded') === 'true' || /close|cerrar|fechar/i.test(label)) return true;
          if ([...root.querySelectorAll('[role="dialog"],.interstitial-view,.chat-view')].some(visible)) return true;
        }
      }
      return false;
    }
    hasOverlay() {
      return Boolean(document.querySelector('dialog[open],cart-drawer.active,.drawer.active,product-modal[open],menu-drawer details[open],header-drawer details[open]')) || this.inboxOpen();
    }

    money(cents) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'es', { style: 'currency', currency: this.dataset.currency || 'USD' }).format(Number(cents) / 100); }
      catch (_) { return `${(Number(cents) / 100).toFixed(2)} ${this.dataset.currency || ''}`.trim(); }
    }
    sync() {
      if (!this._button || !this.bindMain()) { this.hidden = true; this.space(); return; }
      const idInput = this._form.querySelector('[name="id"]');
      const id = idInput?.value || '';
      let variant = this._variants.find(item => String(item.id) === String(id));
      try {
        const selected = JSON.parse(this._info.querySelector('variant-selects [data-selected-variant]')?.textContent || 'null');
        if (selected && String(selected.id) === String(id)) variant = selected;
      } catch (_) { /* Native form remains the authority. */ }
      if (this._pendingId && String(id) === this._pendingId) {
        this._pendingId = null; clearTimeout(this._pendingTimer);
        if (this._pendingQuantity != null) { this._qty.value = this._pendingQuantity; this.writeQuantity(true); this._pendingQuantity = null; }
        if (this._status) this._status.textContent = '';
      }
      this.syncControls(variant);
      if (this._trigger) this._trigger.disabled = this.pickerMode() !== 'panel';
      const display = this._displayMap.get(String(variant?.id));
      if (this._picker) this._picker.querySelectorAll('[data-ovs-option]').forEach(option => {
        const selected = option.dataset.ovsOption === String(variant?.id);
        const pressed = selected ? 'true' : 'false';
        if (option.getAttribute('aria-pressed') !== pressed) option.setAttribute('aria-pressed', pressed);
        if (selected && typeof variant.available === 'boolean') option.disabled = !variant.available;
      });
      const busy = Boolean(this._pendingId) || this._mainButton.classList.contains('loading') || this._mainButton.getAttribute('aria-busy') === 'true';
      const disabled = this._mainButton.disabled || this._mainButton.getAttribute('aria-disabled') === 'true' || busy || !id || !variant || variant.available === false;
      this._button.disabled = disabled;
      this._button.setAttribute('aria-busy', busy ? 'true' : 'false');
      this._button.textContent = this._pendingId ? this.dataset.updating : busy ? this._button.dataset.busy : variant?.available === false ? this._button.dataset.soldout : disabled ? this._button.dataset.invalid : this._button.dataset.label;
      const nativePrice = this._info.querySelector('[data-ot-current-price]');
      const validNative = nativePrice && !nativePrice.closest('.hidden,[hidden]');
      if (this._price) {
        this._price.hidden = !variant || !this.deviceFlag('price');
        if (variant) this.setText(this._price, validNative ? this.withCurrency(nativePrice.textContent.trim()) : display?.price || this.withCurrency(this.money(variant.price)));
      }
      if (this._compare) {
        this._compare.hidden = !this.deviceFlag('compare') || !variant || !(variant.compare_at_price > variant.price);
        if (!this._compare.hidden) {
          const nativeCompare = this._info.querySelector('.otp-price-line s.price-item--regular');
          this.setText(this._compare, nativeCompare && !nativeCompare.closest('.hidden,[hidden]') ? this.withCurrency(nativeCompare.textContent.trim()) : display?.compare || this.withCurrency(this.money(variant.compare_at_price)));
        }
      }
      if (this._saving) {
        const hasSaving = Boolean(variant && variant.compare_at_price > variant.price && Number(variant.compare_at_price) > 0);
        this._saving.hidden = !hasSaving || !this.deviceFlag('saving');
        if (hasSaving) {
          const saving = Math.round((Number(variant.compare_at_price) - Number(variant.price)) * 100 / Number(variant.compare_at_price));
          this.setText(this._saving, `Ahorra ${saving}%`);
        }
      }
      if (this._variantText) {
        const pieces = [];
        if (this.dataset.showVariant === 'true' && variant?.title && !['Default Title', 'Título predeterminado'].includes(variant.title)) pieces.push(variant.title);
        if (!this._qty && this.dataset.showQuantity === 'true') {
          const input = this._info.querySelector('input[name="quantity"]') || [...document.querySelectorAll('input[name="quantity"]')].find(input => input.getAttribute('form') === this._form.id);
          pieces.push(`Cantidad: ${input?.value || 1}`);
        }
        this._variantText.hidden = !pieces.length;
        this.setText(this._variantText, pieces.join(' · '));
        this._variantText.title = pieces.join(' · ');
      }
      if (this._photo) {
        const source = display?.image || this.thumbnail(variant?.featured_image?.src) || this._defaultPhoto;
        if (source && this._photo.getAttribute('src') !== source) this._photo.setAttribute('src', source);
      }
      const enabled = this._media.matches ? this.dataset.mobile === 'true' : this.dataset.desktop === 'true';
      if ((!enabled || this.pickerMode() !== 'panel') && this._picker?.open) this.closePicker();
      this.observeInbox();
      const suspended = this.hasOverlay();
      this.dataset.ovsSuspended = String(suspended);
      this.inert = suspended;
      const mode = this.dataset.display || 'after';
      const passed = mode === 'always' || (mode === 'button'
        ? this._mainButton.getBoundingClientRect().bottom <= 0
        : this.passedScrollThreshold());
      const editorPreview = window.Shopify?.designMode && this.dataset.editorPreview === 'true';
      this.hidden = !(enabled && (passed || editorPreview));
      this.space();
    }

    passedScrollThreshold() {
      const raw = this._media.matches ? this.dataset.scrollMobile : this.dataset.scrollDesktop;
      const parsed = Number(raw);
      const percent = raw !== undefined && raw !== '' && Number.isFinite(parsed)
        ? Math.max(0, Math.min(100, parsed)) : 30;
      if (percent === 0) return true;
      const root = document.scrollingElement || document.documentElement;
      const viewport = document.documentElement.clientHeight || window.innerHeight;
      // The reserved spacer must not increase the denominator when this bar appears.
      // Otherwise the bar can repeatedly hide/show right at the threshold.
      const spacer = this._spacer ? Math.round(this._spacer.getBoundingClientRect().height) : 0;
      const distance = Math.max(0, root.scrollHeight - viewport - spacer);
      if (distance <= 0) return false;
      const position = Math.max(0, window.scrollY || root.scrollTop || 0);
      return position >= Math.ceil(distance * percent / 100);
    }

    space() {
      if (!this._spacer) return;
      const height = !this.hidden && this.dataset.reserve === 'true' ? Math.ceil(this.getBoundingClientRect().height + (parseFloat(getComputedStyle(this).bottom) || 0)) : 0;
      const value = `${height}px`;
      if (this._spacer.style.height !== value) this._spacer.style.height = value;
    }
    submit() {
      this.writeQuantity(true);
      this.sync();
      if (this._button.disabled || !this._form || !this._mainButton || this._submitting) return;
      if (this._form.reportValidity && !this._form.reportValidity()) {
        this._mainButton.scrollIntoView({ block: 'center', behavior: 'auto' });
        return;
      }
      this._submitting = true;
      this._button.disabled = true;
      try {
        if (typeof this._form.requestSubmit === 'function') this._form.requestSubmit(this._mainButton);
        else this._mainButton.click();
      } finally {
        this._submitting = false;
        this.schedule();
      }
    }
  }
  customElements.define('ov-sticky-buy', OvStickyBuy);
})();
